import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [originalRoles, setOriginalRoles] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRol, setSelectedRol] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    descripcion_rol: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  // Decodificar token JWT para obtener id_rol
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdRol(payload.id_rol || null);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        const errorMsg = 'Error al decodificar el token de usuario';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalizar datos de rol
  const normalizeRol = (rol) => {
    if (!rol || !rol.id_rol) {
      console.warn('Rol inválido recibido:', rol);
      return null;
    }
    return {
      id_rol: rol.id_rol ?? null,
      descripcion_rol: rol.descripcion_rol ?? '',
      createdAt: rol.createdAt ?? null,
      updatedAt: rol.updatedAt ?? null,
    };
  };

  // Obtener roles
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdRol) {
        console.log('Esperando id_rol del usuario...');
        return;
      }
      try {
        setLoading(true);
        const rolesRes = await api.get('/roles/');
        console.log('Datos crudos de GET /roles:', rolesRes.data);
        const rolesData = Array.isArray(rolesRes.data)
          ? rolesRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const normalizedRoles = rolesData
          .map(normalizeRol)
          .filter(rol => rol !== null && rol.id_rol);
        console.log('Roles normalizados:', normalizedRoles);
        setRoles(normalizedRoles);
        setOriginalRoles(normalizedRoles);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        const errorMsg = 'Error al cargar datos: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdRol]);

  // Buscar roles en frontend
  useEffect(() => {
    const searchRoles = () => {
      console.log('Buscando con término:', searchTerm);
      console.log('Roles originales:', originalRoles);
      if (!searchTerm) {
        setRoles(originalRoles);
        console.log('Restaurando roles:', originalRoles);
        return;
      }
      const filteredRoles = originalRoles
        .filter(rol => rol && rol.id_rol && rol.descripcion_rol)
        .filter(rol => rol.descripcion_rol.toLowerCase().includes(searchTerm.toLowerCase()));
      console.log('Roles filtrados:', filteredRoles);
      setRoles(filteredRoles);
      setPage(0);
    };
    searchRoles();
  }, [searchTerm, originalRoles]);

  // Abrir modal para crear/editar rol
  const handleOpenModal = (rol = null) => {
    if (userIdRol !== 1) {
      setSnackbarMessage('No autorizado para crear o editar roles');
      setSnackbarOpen(true);
      return;
    }
    setSelectedRol(rol);
    setFormData(
      rol
        ? normalizeRol(rol) || {
            descripcion_rol: '',
          }
        : {
            descripcion_rol: '',
          }
    );
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar rol (crear o editar)
  const handleSave = async () => {
    try {
      const payload = {
        descripcion_rol: formData.descripcion_rol || null,
      };
      console.log('Guardando rol, payload:', payload);
      if (selectedRol) {
        const response = await api.put(`/roles/${selectedRol.id_rol}`, payload);
        console.log('Respuesta de PUT /roles:', response.data);
        const updatedRol = normalizeRol(response.data);
        if (updatedRol) {
          const newRoles = roles.map(r => (r.id_rol === selectedRol.id_rol ? updatedRol : r));
          setRoles(newRoles);
          setOriginalRoles(newRoles);
          setSnackbarMessage('Rol actualizado exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/roles/', payload);
        console.log('Respuesta de POST /roles:', response.data);
        const newRol = normalizeRol(response.data);
        if (newRol) {
          const newRoles = [...roles, newRol];
          setRoles(newRoles);
          setOriginalRoles(newRoles);
          setSnackbarMessage('Rol creado exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar rol:', error);
      const errorMsg = 'Error al guardar rol: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar rol
  const handleDelete = async (id) => {
    if (userIdRol !== 1) {
      setSnackbarMessage('No autorizado para eliminar roles');
      setSnackbarOpen(true);
      return;
    }
    if (!window.confirm('¿Estás seguro de que deseas eliminar este rol?')) return;
    try {
      console.log('Enviando DELETE /roles/', id);
      const response = await api.delete(`/roles/${id}`);
      console.log('Respuesta de DELETE /roles:', response.data);
      const newRoles = roles.filter(r => r.id_rol !== id);
      setRoles(newRoles);
      setOriginalRoles(newRoles);
      setSnackbarMessage('Rol eliminado exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (error) {
      console.error('Error al eliminar rol:', error);
      const errorMsg = 'Error al eliminar rol: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedRoles = roles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Roles
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdRol ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : roles.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron roles. {userIdRol === 1 ? 'Crea un nuevo rol para comenzar.' : 'Contacta a un administrador.'}
            </Typography>
            {userIdRol === 1 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal()}
                sx={{ mt: 2 }}
              >
                Nuevo Rol
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              {userIdRol === 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal()}
                  >
                    Nuevo Rol
                  </Button>
                </Box>
              )}
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  boxShadow: 3,
                }}
              >
                <Table
                  sx={{
                    tableLayout: 'auto',
                  }}
                  aria-label="tabla de roles"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Descripción
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Creado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Actualizado
                      </TableCell>
                      {userIdRol === 1 && (
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                          Acciones
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRoles.map((row) => {
                      if (!row || !row.id_rol) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      return (
                        <TableRow
                          key={row.id_rol}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.id_rol}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, fontWeight: 'bold' }}>
                            {row.descripcion_rol || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.updatedAt)}
                          </TableCell>
                          {userIdRol === 1 && (
                            <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                              <IconButton
                                onClick={() => handleOpenModal(row)}
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDelete(row.id_rol)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(roles.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(roles.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedRol ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Descripción"
              fullWidth
              value={formData.descripcion_rol}
              onChange={(e) => setFormData({ ...formData, descripcion_rol: e.target.value })}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Roles;